import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageLoader from '../components/PageLoader';
import api from '../utils/api';
import { getCityFromHost } from '../utils/getCityFromHost';
import Pagination from '../components/Pagination';
import { showSuccess, showError } from '../utils/toast';
import { isUserLoggedIn } from '../utils/auth';
import BusinessCard from '../components/BusinessCard';

const FeaturedBusinesses = () => {
  const [featuredBusinesses, setFeaturedBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [favouriteIds, setFavouriteIds] = useState(new Set());
  const city = getCityFromHost();
  const cityName = city ? city.charAt(0).toUpperCase() + city.slice(1) : '';

  useEffect(() => {
    fetchFeaturedBusinesses();
  }, [currentPage]);

  useEffect(() => {
    if (!isUserLoggedIn()) return;
    api.get('/favourites').then((r) => {
      const list = r.data?.favourites || [];
      setFavouriteIds(new Set(list.map((b) => (b._id || b).toString())));
    }).catch(() => { });
  }, []);

  const fetchFeaturedBusinesses = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 20
      };
      const response = await api.get('/businesses/featured', { params });

      if (response.data && response.data.businesses) {
        setFeaturedBusinesses(response.data.businesses);
        setPagination(response.data.pagination);
      } else if (Array.isArray(response.data)) {
        setFeaturedBusinesses(response.data);
        setPagination(null);
      } else {
        setFeaturedBusinesses([]);
        setPagination(null);
      }
    } catch (error) {
      console.error('Error fetching featured businesses:', error);
      setFeaturedBusinesses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFavourite = async (id) => {
    try {
      await api.post(`/favourites/${id}`);
      setFavouriteIds((prev) => new Set([...prev, id.toString()]));
      showSuccess('Added to favourites');
    } catch (e) {
      showError(e.response?.data?.message || 'Could not add to favourites');
    }
  };
  const handleRemoveFavourite = async (id) => {
    try {
      await api.delete(`/favourites/${id}`);
      setFavouriteIds((prev) => {
        const next = new Set(prev);
        next.delete(id.toString());
        return next;
      });
      showSuccess('Removed from favourites');
    } catch (e) {
      showError(e.response?.data?.message || 'Could not remove from favourites');
    }
  };

  return (
    <>
      <SEO
        title="Featured Businesses"
        description={`Browse all featured businesses in ${cityName || 'Fatehabad'}. Handpicked businesses recommended for you.`}
        canonical="/featured"
      />

      <div className="container-custom py-8 md:py-16">
        {/* Header */}
        <div className="text-center mb-12 animate-fade-in">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-50 mb-4">
            Featured Businesses
          </h1>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Handpicked businesses recommended for you in {cityName || 'Fatehabad'}
          </p>
        </div>

        {loading ? (
          <PageLoader message="Loading featured businesses..." />
        ) : featuredBusinesses.length > 0 ? (
          <>
            {/* Featured Businesses Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 2xl:grid-cols-3 gap-0 md:gap-6 divide-y md:divide-y-0 divide-gray-100 dark:divide-gray-800 mb-12">
              {featuredBusinesses.map((business) => (
                <BusinessCard
                  key={business._id}
                  business={business}
                  favouriteIds={favouriteIds}
                  onAddFavourite={handleAddFavourite}
                  onRemoveFavourite={handleRemoveFavourite}
                />
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalItems={pagination.totalItems}
                  itemsPerPage={pagination.itemsPerPage}
                  itemsOnCurrentPage={pagination.itemsOnCurrentPage}
                  hasNextPage={pagination.hasNextPage}
                  hasPrevPage={pagination.hasPrevPage}
                  nextPage={pagination.nextPage}
                  prevPage={pagination.prevPage}
                  onPageChange={(page) => {
                    setCurrentPage(page);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                />
              </div>
            )}
          </>
        ) : (
          <div className="card p-12 text-center">
            <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-50 mb-2">No Featured Businesses Yet</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Check back later for featured businesses in {cityName || 'Fatehabad'}.
            </p>
            <Link
              to="/"
              className="btn-primary inline-block"
            >
              Browse All Businesses
            </Link>
          </div>
        )}
      </div>
    </>
  );
};

export default FeaturedBusinesses;


